/**
 * Export the payroll review (employees + Fair Work rates) to a formatted
 * .xlsx. Includes name, grade, employment type, hours, current vs minimum
 * rate, and a compliance verdict. Only employees with a calculated rate are
 * included with rate columns; others appear with their Xero details only.
 */

import type { EmployeeReviewRow, MinWageLookup } from '@/services/api/payroll';

interface ExportArgs {
  clientName: string;
  xeroOrgName?: string | null;
  rows: EmployeeReviewRow[];
  lookups: Record<string, MinWageLookup | 'loading' | 'error'>;
}

const HEADERS = [
  'Employee', 'Job title', 'Employment', 'Hours/wk', 'Age',
  'Award', 'Grade', 'Current $/hr', 'Min $/hr', 'Status', 'Difference $/hr', 'Confidence',
];

function fmtMoney(n: number | null | undefined): number | string {
  return typeof n === 'number' ? Number(n.toFixed(2)) : '';
}

export async function exportPayrollToExcel({ clientName, xeroOrgName, rows, lookups }: ExportArgs) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'RoniDoc AI';
  wb.created = new Date();

  const ws = wb.addWorksheet('Payroll review', {
    views: [{ state: 'frozen', ySplit: 4 }],
  });

  // Title block
  ws.mergeCells(1, 1, 1, HEADERS.length);
  const title = ws.getCell(1, 1);
  title.value = `${clientName} - Fair Work minimum wage review`;
  title.font = { bold: true, size: 14, color: { argb: 'FF1F2937' } };

  ws.mergeCells(2, 1, 2, HEADERS.length);
  const sub = ws.getCell(2, 1);
  sub.value = `${xeroOrgName ? xeroOrgName + ' · ' : ''}Generated ${new Date().toLocaleString('en-AU')}`;
  sub.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } };

  // Header row (row 4)
  const headerRow = ws.getRow(4);
  HEADERS.forEach((h, idx) => {
    const c = headerRow.getCell(idx + 1);
    c.value = h;
    c.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };
    c.alignment = { vertical: 'middle', horizontal: idx >= 3 ? 'center' : 'left' };
    c.border = { bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } } };
  });
  headerRow.height = 20;

  // Data rows
  let r = 5;
  let belowCount = 0;
  for (const row of rows) {
    const emp = row.employee;
    const lk = lookups[emp.employeeId];
    const data = lk && typeof lk !== 'string' ? lk : null;

    const name = `${emp.firstName} ${emp.lastName}`.trim() || '(no name)';
    const minRate = data?.hourlyRate ?? null;
    const current = emp.currentPayRate ?? null;
    const status = data
      ? (data.compliance === 'compliant' ? 'OK' : data.compliance === 'below' ? 'BELOW MIN' : '-')
      : '';
    const diff = (minRate != null && current != null) ? Number((current - minRate).toFixed(2)) : '';
    const grade = data?.matchedRate?.classification ?? (row.savedGrade ?? '');

    const values = [
      name,
      emp.jobTitle ?? '',
      emp.employmentBasis ?? '',
      emp.hoursPerWeek ?? '',
      emp.ageYears ?? '',
      data?.awardCode ?? row.suggestedAward.code ?? '',
      grade,
      fmtMoney(current),
      fmtMoney(minRate),
      status,
      diff,
      data ? (data.gradeConfirmed ? 'Confirmed' : data.confidence) : '',
    ];

    const xlRow = ws.getRow(r);
    values.forEach((v, idx) => {
      const c = xlRow.getCell(idx + 1);
      c.value = v as any;
      c.alignment = { horizontal: idx >= 3 ? 'center' : 'left', vertical: 'middle' };
      if (idx >= 7 && idx <= 8 && typeof v === 'number') c.numFmt = '$#,##0.00';
      if (idx === 10 && typeof v === 'number') c.numFmt = '$#,##0.00;[Red]-$#,##0.00';
    });

    // Highlight below-minimum rows
    if (data?.compliance === 'below') {
      belowCount++;
      xlRow.eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
      });
      xlRow.getCell(10).font = { bold: true, color: { argb: 'FFB91C1C' } };
    } else if (data?.compliance === 'compliant') {
      xlRow.getCell(10).font = { color: { argb: 'FF047857' } };
    }
    r++;
  }

  // Summary line
  r += 1;
  const summary = ws.getRow(r);
  summary.getCell(1).value = `${rows.length} employees · ${belowCount} below minimum`;
  summary.getCell(1).font = { bold: true, size: 11, color: belowCount > 0 ? { argb: 'FFB91C1C' } : { argb: 'FF047857' } };

  // Column widths
  const widths = [26, 20, 13, 9, 6, 12, 30, 12, 12, 12, 14, 12];
  widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  // Download
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safe = clientName.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  a.download = `payroll-review-${safe}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
